import React, { useContext, useState, useEffect } from "react";
import { useHistory, Route, Switch, useLocation } from "react-router-dom";
import {
  Dimmer,
  SidebarPushable,
  SidebarPusher,
  Modal,
} from "semantic-ui-react";
import Routes from "./Routes";
import { AppContext } from "./Context/AppContext";
import Header from "./components/cmp-header/Header";
import Footer from "./components/cmp-footer/Footer";
import Sidebar from "./containers/admin/sidebar/sidebar";
import "semantic-ui-css/semantic.min.css";
import "./App.css";
import SideSliderCart from "./components/cmp-side-slider-cart/cmp-side-slider-cart";
import { transform } from "lodash";
import AppRoute from "./components/cmp-app-route/AppRoute";
import Buildingheatmapreport from "./containers/customer/customer-home/buildingheatmap-report/buildingheatmap-report";
import IdleTimerContainer from "./components/cmp-idel-timer-container/IdelTimerContainer";
import CMP_IMPERSONATION_TIMEOUT from "./components/cpm-impersonation-session/cpm-impersonation-session";
import CMP_OUTDATED_VERSION from "./components/cpm-outdated-version-modal/cpm-outdated-version-modal";
function App() {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const {
    userInfo,
    bInfo,
    sliderCart,
    cartItems,
    impersonatedUser,
    defaultPane,
    checkedUnitsData,
    selected_units,
    isAppVersionOutdated,
    selectedBuilding,
    sbuildingData,
    orderFilter,
  } = useContext(AppContext);
  const [is_app_version_outdated, set_is_app_version_outdated] =
    isAppVersionOutdated;
  const [, setSelectedBuilding] = selectedBuilding;
  const [, setSingleBuildingData] = sbuildingData;
  const [, set_order_filter] = orderFilter;
  const [, set_add_unit] = selected_units;
  const [, setCheckedUnits] = checkedUnitsData;
  const [, setCartItems] = cartItems;
  const [, set_default_pane] = defaultPane;
  const [, setIsImpersonatedUser] = impersonatedUser;
  const [user, setUser] = userInfo;
  const [building_info, setBuildingInfo] = bInfo;
  const [var_is_open, set_is_open] = useState(false);
  const [enable_selection, set_enable_selection] = useState(false);
  const [var_window_size, set_window_size] = useState(0);
  const screenWidth = window.innerWidth;
  const [sideSliderCart, setSideSliderCart] = sliderCart;
  const isImpersonating = localStorage.getItem("impersonation") === "true";
  const [var_mdl_impersonation, set_mdl_impersonation] = useState(false);
  const [var_redirect_path, set_redirect_path] = useState("/");
  const history = useHistory();
  const location = useLocation();

  /* EVENT LISTENERS
	---------------------------------------------------------------------------------*/

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== "impersonation") return;
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        const isScheduler = userData?.role === "scheduler";
        const isImpersonationMissing = !localStorage.getItem("impersonation");

        if (
          (isScheduler &&
            isImpersonationMissing &&
            history.location.pathname === "/home") ||
          history.location.pathname === "/order-review" ||
          history.location.pathname.includes("/order-request")
        ) {
          set_mdl_impersonation(true);
        }
      } catch (err) {
        console.error("Error reading localStorage:", err);
      }
    };
    // Listen for changes from OTHER tabs
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      set_is_open(false);
    });
  }, [history]);

  useEffect(() => {
    const handleResize = () => {
      set_window_size(screenWidth);
      if (var_window_size > 767) {
        set_is_open(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [var_window_size, screenWidth]);

  useEffect(() => {
    if (user && is_app_version_outdated) {
      logOutAllUsers();
    }
  }, [is_app_version_outdated]);

  /* EVENT FUNCTIONS
	---------------------------------------------------------------------------------*/
  const toggle = () => {
    set_is_open(!var_is_open);
  };

  const dimmerHide = () => {
    set_is_open(false);
  };

  const goBackToScheduler = () => {
    const scheduler = JSON.parse(localStorage.getItem("scheduler_user"));
    if (scheduler) {
      setIsImpersonatedUser(true);
      setUser(scheduler);
      set_default_pane(0);
      setCartItems(null);
      setSideSliderCart(false);
      setCheckedUnits({});
      set_add_unit([{ unit_number: "", followup: false, key: 0 }]);
      localStorage.setItem("user", JSON.stringify(scheduler));
      const buildingData = JSON.parse(localStorage.getItem("building") || "[]");
      const buildingId = buildingData?.[0]?.value;
      localStorage.removeItem("impersonation");
      localStorage.removeItem("building");
      history.replace(`/scheduler-home`);
    }
  };

  const logOutAllUsers = () => {
    // This function is called when the app detects that the current version is outdated.
    // It logs out the user and redirects to the appropriate login page based on the user's role.
    if (
      user.role === "scheduler" ||
      localStorage.getItem("impersonation") === "true"
    ) {
      localStorage.removeItem("impersonation");
      history.replace("/scheduler");
    } else if (user.role === "technician") {
      history.replace("/technician");
    } else if (user.role === "customer") {
      setBuildingInfo(null);
      history.replace("/");
    } else {
      history.replace("/admin");
    }

    localStorage.removeItem("building");
    localStorage.removeItem("user");
    setUser(null);
    setSelectedBuilding(null);
    setSingleBuildingData([]);
    setCartItems(null);
    setCheckedUnits({});
    set_default_pane(0);
    set_order_filter(3);
    set_add_unit([{ unit_number: "", followup: false, key: 0 }]);
  };

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      {location.pathname !== "/buildingheatmapreport" && (
        <SidebarPushable style={{ overflow: "hidden", transform: "none" }}>
          <SideSliderCart visible={sideSliderCart} />
          <SidebarPusher>
            {user ? (
              <>
                {(user?.role === "customer" || user?.role === "scheduler") && (
                  <IdleTimerContainer />
                )}
                <div className="app-wrapper">
                  <Header
                    user={user}
                    setUser={setUser}
                    building_info={building_info}
                    setBuildingInfo={setBuildingInfo}
                    navToggle={toggle}
                  />
                  <main
                    className={
                      "app-main" +
                      (user.role === "admin" ? " app-main-admin" : "")
                    }
                  >
                    <div className="app-container">
                      {user.role === "admin" && (
                        <>
                          <Dimmer
                            className="mobile-nav-dimmer"
                            active={var_is_open}
                            onClickOutside={() => dimmerHide()}
                            page
                            inverted
                            blurring
                          />

                          <Sidebar navOpen={var_is_open} />
                        </>
                      )}

                      <Routes appProps={{}} />
                    </div>
                  </main>

                  <Footer />
                </div>
              </>
            ) : (
              <Routes appProps={{}} />
            )}
          </SidebarPusher>
        </SidebarPushable>
      )}
      {location.pathname === "/buildingheatmapreport" && (
        <Switch>
          <Route component={Buildingheatmapreport} />
        </Switch>
      )}

      {/***** MODAL: Impersonation Session **************************************************************************/}

      {user &&
        var_mdl_impersonation &&
        (history.location.pathname === "/home" ||
          history.location.pathname === "/order-review" ||
          history.location.pathname.includes("/order-request")) && (
          <Modal
            id="mdl-update-scheduler"
            style={{ overflow: "visible" }}
            dimmer={"inverted"}
            open={var_mdl_impersonation}
            onClose={() => {
              goBackToScheduler();
              set_mdl_impersonation(false);
            }}
          >
            <CMP_IMPERSONATION_TIMEOUT
              set_mdl_open={set_mdl_impersonation}
              modalTitle="Impersonation Session Expired"
              modalMessage="Your impersonation session is no longer valid. Please re-initiate
          impersonation to proceed."
              onConfirm={() => {
                goBackToScheduler();
              }}
            />
          </Modal>
        )}

      {/***** END MODAL: Impersonation Session **************************************************************************/}

      {/***** MODAL: Outdated Version **************************************************************************/}

      {is_app_version_outdated && (
        <Modal
          id="mdl-update-scheduler"
          style={{ overflow: "visible" }}
          dimmer={"inverted"}
          open={is_app_version_outdated}
          onClose={() => {
            set_is_app_version_outdated(false);
            window.location.href =
              window.location.origin +
              window.location.pathname +
              "?v=" +
              Date.now();
            window.location.reload();
          }}
        >
          <CMP_OUTDATED_VERSION
            set_mdl_open={set_is_app_version_outdated}
            modalTitle="Outdated Version"
            modalMessage="A new version of the app is available.Please reload to continue with the latest version."
            isUserAvailable={!!user}
            onConfirm={() => {
              set_is_app_version_outdated(false);
              window.location.href =
                window.location.origin +
                window.location.pathname +
                "?v=" +
                Date.now();
              window.location.reload();
            }}
          />
        </Modal>
      )}

      {/***** END MODAL: Outdated Version **************************************************************************/}
    </>
  );
}

export default App;
